import { useOktaAuth } from '@okta/okta-react';
import OktaSignIn, { RenderResultSuccess } from '@okta/okta-signin-widget';
import '@okta/okta-signin-widget/dist/css/okta-sign-in.min.css';
import React, { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { IdpType, OktaMetaData } from 'api/account';
import { Button } from 'components/atoms/button';
import { ReactComponent as Google } from 'components/atoms/vendor-loign/google.svg';
import { ClassNameOnlyProps } from 'components/ozone-component';
import { BodyShort01 } from 'components/styles';
import { CONFIG } from 'config/okta';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { getEnabledIdp } from '../saml';

interface OktaSignInWidgetProps extends ClassNameOnlyProps {
  oktaMetaData: OktaMetaData;
}
const OktaSignInWidget: FC<OktaSignInWidgetProps> = ({
  oktaMetaData,
  className,
}) => {
  const signWithSSO = useTranslation('sign_in.sso');
  const { oktaAuth } = useOktaAuth();

  useEffect(() => {
    const { pkce, issuer, redirectUri, scopes, clientId } = CONFIG(
      oktaMetaData.issuerDomain,
      oktaMetaData.clientId,
    );
    const widget = new OktaSignIn({
      /**
       * Note: when using the Sign-In Widget for an OIDC flow, it still
       * needs to be configured with the base URL for your Okta Org. Here
       * we derive it from the given issuer for convenience.
       */
      baseUrl: issuer?.split('/oauth2')[0],
      clientId,
      redirectUri,
      features: {
        showPasswordToggleOnSignInPage: true,
      },
      i18n: {
        en: {
          'primaryauth.title': 'Sign in',
          'primaryauth.username.placeholder': 'Email',
          'primaryauth.username.tooltip': 'Your email id',
          'error.username.required': 'Please enter an email id',
          'errors.E0000004': 'Invalid email or password',
        },
      },
      authParams: {
        pkce,
        issuer,
        display: 'page',
        responseMode: pkce ? 'query' : 'fragment',
        scopes,
      },
      transformUsername: (username: string) => {
        if (
          oktaMetaData?.userName &&
          username.includes(oktaMetaData?.userName)
        ) {
          return `${username.replaceAll(oktaMetaData?.userName, '')}${
            oktaMetaData?.userName || ''
          }`;
        }

        return `${username}${oktaMetaData?.userName || ''}`;
      },
    });

    widget.on('ready', () => {
      const emailInput = document.querySelector<HTMLInputElement>(
        '#okta-signin-username',
      );

      if (
        oktaMetaData?.userName &&
        emailInput?.value.includes(oktaMetaData?.userName)
      ) {
        emailInput?.setAttribute(
          'value',
          emailInput.value.replaceAll(oktaMetaData.userName, ''),
        );
      }
    });
    widget.renderEl(
      { el: '#sign-in-widget' },
      (res) => {
        oktaAuth.handleLoginRedirect((res as RenderResultSuccess).tokens);
      },
      (err: Error) => {
        throw err;
      },
    );

    return () => {
      widget.remove();
    };
  }, [oktaAuth, oktaMetaData, signWithSSO]);

  return <div className={className} id="sign-in-widget" />;
};

// NOTE: The following is our layout, realised we may not need this if we are using Okta's Widget.
// Once enabled, make sure to import components from antd.
// Refer: https://developer.okta.com/code/react/okta_react/
// const OktaSignInWidget: FC = () => (
//   <div className="space-align-container">
//     <div className="space-align-block">
//       <h1 className="text-center">
//         Welcome
//       </h1>
//       <Form className="login-form" layout="vertical">
//         <Form.Item
//           name="email"
//           label="Work email"
//           rules={[{ required: true, message: 'Please input your work email!' }]}
//         >
//           <Input placeholder="Username" />
//         </Form.Item>
//         <Form.Item
//           name="password"
//           label="Password"
//           rules={[{ required: true, message: 'Please input your password!' }]}
//         >
//           <Input type="password" placeholder="********" />
//         </Form.Item>
//         <Form.Item>
//           <span>Forgot password?</span>
//         </Form.Item>
//         <Form.Item>
//           <Button type="primary" htmlType="submit" className="login-form-button">
//             Sign in
//           </Button>
//         </Form.Item>
//       </Form>
//     </div>
//   </div>
// );

interface SignInWidgetProps extends OktaSignInWidgetProps {
  isSamlMandatory: boolean;
  isPartner: boolean;
}

const Hint = styled.p`
  ${BodyShort01};
  color: var(--gray-50);
`;

const Wrapper = styled.div`
  display: flex;
  gap: var(--spacing-03);
  align-items: stretch;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  width: 400px;
`;

export const SignInWidget: FC<SignInWidgetProps> = ({
  oktaMetaData,
  className,
  isSamlMandatory,
  isPartner,
}) => {
  const { oktaAuth } = useOktaAuth();
  const idp = getEnabledIdp(oktaMetaData);
  const translate = useTranslate();

  if (isSamlMandatory && idp && !isPartner) {
    return (
      <Wrapper>
        <StyledButton
          key={idp.idpId}
          icon={idp.idpType === IdpType.Google && <Google />}
          onClick={() => {
            oktaAuth.signInWithRedirect({
              idp: idp.idpId,
            });
          }}
        >
          {idp.idpType === IdpType.Google
            ? translate('google.sign-in')
            : translate('sign_in.sso')}
        </StyledButton>
        <Hint>
          <FormattedMessage id="sign_in.sso.mandate.hint" />
        </Hint>
      </Wrapper>
    );
  }

  return <OktaSignInWidget oktaMetaData={oktaMetaData} className={className} />;
};
